/**
 * 業務ライン別ロール (OrientDB の ORole 名) と、メニュー可視性を決める
 * ケーパビリティの定義。ロール名は backend の ORole.name と一致させる
 * (7 ロールの seed は active-promotion.core 側の起動時ブートストラップで行う)。
 *
 * 設計方針:
 *  - 画面/メニューは「ケーパビリティ」で出し分ける (ロール名直参照を避ける)。
 *  - 各ロール → 保有ケーパビリティの対応表 (ROLE_CAPS) を一元管理する。
 *  - システム管理者は全ケーパビリティを持つ (全ライン管理を見る)。
 *  - 各ラインの管理メニューは、そのラインの管理者ロール (と システム管理者) だけに
 *    見せる。旧「管理者」「admin」の全権後方互換は撤去した (これらを持つだけの
 *    アカウントに管理メニューが見えてしまうため)。管理担当には 7 ロールを割り当てる。
 *  - 旧「ユーザ」だけは顧客の一般アクセス維持のため一般ケーパビリティを暫定付与する。
 */

export const CAPS = {
    KAITORIYA_VIEW: 'kaitoriya:view',
    KAITORIYA_ADMIN: 'kaitoriya:admin',
    KOBE_GENERAL: 'kobe:general',
    KOBE_ADMIN: 'kobe:admin',
    ONLINE_GENERAL: 'online:general',
    ONLINE_ADMIN: 'online:admin',
    // システム管理 (ユーザ/役割管理)。システム管理者(と旧管理者)のみが持つ。
    SYSTEM: 'system:admin',
} as const;

export type Capability = typeof CAPS[keyof typeof CAPS];

// Set の spread/Array.from は AOT prod で空になる事例があるため、
// プレーンオブジェクトから値配列を得る (Object.values は安全)。
export const ALL_CAPS: Capability[] = Object.keys(CAPS).map((k) => (CAPS as any)[k]);

/** ORole 名 (7 分類)。backend の seed と一致させること。 */
export const ROLES = {
    KAITORIYA_USER: '買取屋ユーザ',
    KAITORIYA_ADMIN: '買取屋管理者',
    KOBE_USER: '神戸オークションユーザ',
    KOBE_ADMIN: '神戸オークション管理者',
    ONLINE_USER: 'オンラインオークションユーザ',
    ONLINE_ADMIN: 'オンラインオークション管理者',
    SYSTEM_ADMIN: 'システム管理者',
} as const;

// 旧「ユーザ」だけは後方互換で残す (顧客の一般アクセス維持)。
// 旧「管理者」「admin」の全権後方互換は撤去 (管理メニューは 7 ロールで厳密制御)。
const LEGACY_USER = 'ユーザ';

const ROLE_CAPS: { [role: string]: Capability[] } = {
    [ROLES.KAITORIYA_USER]: [CAPS.KAITORIYA_VIEW],
    [ROLES.KAITORIYA_ADMIN]: [CAPS.KAITORIYA_VIEW, CAPS.KAITORIYA_ADMIN],
    [ROLES.KOBE_USER]: [CAPS.KOBE_GENERAL],
    [ROLES.KOBE_ADMIN]: [CAPS.KOBE_GENERAL, CAPS.KOBE_ADMIN],
    [ROLES.ONLINE_USER]: [CAPS.ONLINE_GENERAL],
    [ROLES.ONLINE_ADMIN]: [CAPS.ONLINE_GENERAL, CAPS.ONLINE_ADMIN],
    [ROLES.SYSTEM_ADMIN]: ALL_CAPS,
    // --- 後方互換 (顧客の一般アクセスのみ) ---
    // 旧 ユーザ は従来 storefront(/user) と kobeac 一般(/kobeac) の両方に
    // アクセスできていたため、その 2 つの「一般」ケーパビリティのみ暫定付与する
    // (管理ケーパビリティは付与しない)。
    [LEGACY_USER]: [CAPS.ONLINE_GENERAL, CAPS.KOBE_GENERAL],
};

/** roles 配列 (ORole[] 相当) が保有する全ケーパビリティ集合。 */
export function capabilitiesForRoles(roles: any): Set<Capability> {
    const set = new Set<Capability>();
    if (Array.isArray(roles)) {
        for (const role of roles) {
            const name = role && role.name != null ? String(role.name) : '';
            const caps = ROLE_CAPS[name];
            if (caps) {
                for (const c of caps) {
                    set.add(c);
                }
            }
        }
    }
    return set;
}

/** roles が指定ケーパビリティ (いずれか) を持つか。 */
export function canWith(roles: any, capability: Capability | Capability[]): boolean {
    const set = capabilitiesForRoles(roles);
    const needed = Array.isArray(capability) ? capability : [capability];
    return needed.some((c) => set.has(c));
}

/** roles が指定ロール名を持つか (完全一致)。 */
export function hasRoleName(roles: any, name: string): boolean {
    if (Array.isArray(roles)) {
        for (const role of roles) {
            if (role && String(role.name) === name) {
                return true;
            }
        }
    }
    return false;
}

/**
 * ケーパビリティに基づく既定の着地ルート。ガードで拒否した際の
 * リダイレクト先や、ログイン直後の振り分けに使う。
 */
export function defaultRouteForRoles(roles: any): string {
    const caps = capabilitiesForRoles(roles);
    // 管理権限を一般権限より優先して着地させる。一般(storefront 等)を先に見ると、
    // 神戸オークション管理者のように「一般 + 管理」を併せ持つユーザが storefront に
    // 着地して管理画面へ辿り着けなくなる。
    if (caps.has(CAPS.ONLINE_ADMIN)) {
        return 'admin';
    }
    if (caps.has(CAPS.KOBE_ADMIN)) {
        return 'kobe';
    }
    if (caps.has(CAPS.KAITORIYA_ADMIN)) {
        return 'kaitoriya';
    }
    // 管理権限が無ければ一般権限で着地。
    if (caps.has(CAPS.ONLINE_GENERAL)) {
        return 'user';
    }
    if (caps.has(CAPS.KOBE_GENERAL)) {
        return 'kobe';
    }
    if (caps.has(CAPS.KAITORIYA_VIEW)) {
        return 'kaitoriya';
    }
    return 'login';
}
